/**
 * RecommendationsPanel
 *
 * Grid of pricing recommendation cards sorted by urgency.
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { PricingRecommendation } from '@/services/metricsAPI';

interface RecommendationsPanelProps {
  recommendations: PricingRecommendation[];
}

const URGENCY_COLORS: Record<PricingRecommendation['urgency'], string> = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  low: 'bg-white/10 text-white/40 border-white/10',
};

const CONFIDENCE_COLORS = {
  high: 'bg-green-500/20 text-green-400',
  medium: 'bg-yellow-500/20 text-yellow-400',
  low: 'bg-white/10 text-white/40',
} as const;

const URGENCY_ORDER: Record<PricingRecommendation['urgency'], number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export function RecommendationsPanel({ recommendations }: RecommendationsPanelProps): JSX.Element {
  const { t } = useTranslation();

  const sorted = [...recommendations].sort(
    (a, b) => URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency]
  );

  return (
    <div className="panel-glass-card-lg">
      <h2 className="section-heading-lg">{t('admin.metrics.recommendations')}</h2>

      {sorted.length === 0 ? (
        <div className="text-white/50 text-center py-8">{t('admin.metrics.noRecommendations')}</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sorted.map((rec, index) => (
            <div
              key={`${rec.operation}-${rec.provider ?? 'all'}-${index}`}
              className="bg-black/20 rounded-lg border border-white/10 p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="font-medium text-white">
                  {rec.operation}
                  {rec.provider && (
                    <span className="text-white/40 text-xs ml-2">({rec.provider})</span>
                  )}
                </div>
                <span
                  className={`px-2 py-1 rounded border text-xs font-medium ${URGENCY_COLORS[rec.urgency]}`}
                >
                  {t(`admin.metrics.${rec.urgency}`)}
                </span>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-white/70">
                  <span>{t('admin.metrics.current')}:</span>
                  <span className="font-mono">
                    {Math.round(rec.current_avg_tokens).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-white/70">
                  <span>{t('admin.metrics.recommended')}:</span>
                  <span className="font-mono">{rec.recommended_tokens.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-white/70">
                  <span>{t('admin.metrics.variance')}:</span>
                  <span
                    className={`font-mono font-medium ${rec.variance_percent > 0 ? 'text-yellow-400' : 'text-green-400'}`}
                  >
                    {rec.variance_percent > 0 ? '+' : ''}
                    {rec.variance_percent.toFixed(1)}%
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                <span className={`px-2 py-1 rounded text-xs ${CONFIDENCE_COLORS[rec.confidence]}`}>
                  {t('admin.metrics.confidence')}: {t(`admin.metrics.${rec.confidence}`)}
                </span>
                <span className="text-white/40 text-xs">
                  {rec.sample_count} {t('admin.metrics.samples').toLowerCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
